<template>
  <div
    class="pt-wrapper"
    :class="isDark ? 'pt-dark' : 'pt-light'"
    :style="{ '--brand': brandColor, '--brand-rgb': brandRgb }"
  >
    <!-- ── Header ─────────────────────────────────── -->
    <div class="pt-header">
      <div class="pt-header-left">
        <!-- Optional title slot -->
        <slot name="title">
          <div
            v-if="title || subtitle"
            style="display: flex; flex-direction: column"
          >
            <span class="pt-title" v-if="title">{{ title }}</span>
            <span class="pt-subtitle" v-if="subtitle">{{ subtitle }}</span>
          </div>
        </slot>
      </div>

      <div class="pt-header-right">
        <!-- Search -->
        <div v-if="searchable" class="pt-search-box">
          <v-icon size="16" class="pt-search-icon">mdi-magnify</v-icon>
          <input
            v-model="searchInput"
            class="pt-search-input"
            :placeholder="searchPlaceholder || 'Search...'"
            @input="onSearchDebounced"
          />
          <button
            v-if="searchInput"
            class="pt-search-clear"
            @click="clearSearch"
          >
            <v-icon size="14">mdi-close</v-icon>
          </button>
        </div>

        <!-- Extra controls slot (filters, export, etc.) -->
        <slot name="header-extra" />

        <!-- Add button slot or default button -->
        <slot name="add-button">
          <button
            v-if="addLabel"
            class="pt-add-btn"
            :style="{ background: brandColor }"
            @click="$emit('add')"
            type="button"
          >
            <v-icon size="16" color="white">mdi-plus</v-icon>
            <span>{{ addLabel }}</span>
          </button>
        </slot>
      </div>
    </div>

    <!-- ── Table ─────────────────────────────────── -->
    <div
      class="pt-table-wrap"
      ref="tableWrap"
      @mousedown="onMouseDown"
      @mouseleave="onMouseLeave"
      @mouseup="onMouseUp"
      @mousemove="onMouseMove"
      @click.capture="onClickCapture"
    >
      <table class="pt-table">
        <thead class="pt-thead">
          <tr>
            <!-- Sr. No. column -->
            <th class="pt-th pt-th-no" style="width:52px">No</th>

            <th
              v-for="col in columns"
              :key="col.key || col.label"
              class="pt-th"
              :class="[
                col.align === 'right' ? 'pt-th-right' : '',
                col.align === 'center' ? 'pt-th-center' : '',
                col.hideOnMobile ? 'pt-hide-mobile' : '',
                col.hideOnTablet ? 'pt-hide-tablet' : '',
              ]"
              :style="col.width ? { width: col.width, minWidth: col.width } : { minWidth: '100px' }"
            >
              {{ col.label }}
            </th>
            <th
              v-if="hasActions"
              class="pt-th pt-th-actions"
              style="width:110px;min-width:90px"
            >
              Actions
            </th>
          </tr>
        </thead>

        <tbody>
          <!-- Loading skeleton -->
          <template v-if="loading">
            <tr v-for="i in skeletonRows" :key="'sk-' + i" class="pt-row">
              <!-- No. skeleton -->
              <td class="pt-td pt-td-no"><div class="pt-shimmer" style="width:26px;height:13px;border-radius:6px"></div></td>
              <td
                v-for="col in columns"
                :key="'skcol-' + col.key"
                class="pt-td"
                :class="[
                  col.hideOnMobile ? 'pt-hide-mobile' : '',
                  col.hideOnTablet ? 'pt-hide-tablet' : '',
                ]"
              >
                <div
                  class="pt-shimmer"
                  :style="{
                    width: col.key === 'avatar' ? '36px' : randomWidth() + '%',
                    height: col.key === 'avatar' ? '36px' : '13px',
                    'border-radius': col.key === 'avatar' ? '50%' : '6px',
                  }"
                ></div>
              </td>
              <td v-if="hasActions" class="pt-td">
                <div style="display: flex; justify-content: flex-end; gap: 6px">
                  <div v-if="showView" class="pt-shimmer" style="width: 28px; height: 28px; border-radius: 8px"></div>
                  <div v-if="showEdit" class="pt-shimmer" style="width: 28px; height: 28px; border-radius: 8px"></div>
                  <div v-if="showDelete" class="pt-shimmer" style="width: 28px; height: 28px; border-radius: 8px"></div>
                </div>
              </td>
            </tr>
          </template>

          <!-- Error state -->
          <tr v-else-if="error">
            <td
              :colspan="columns.length + (hasActions ? 2 : 1)"
              class="pt-empty"
            >
              <div class="pt-empty-inner">
                <v-icon size="40" color="#EF4444"
                  >mdi-alert-circle-outline</v-icon
                >
                <span class="pt-empty-title" style="color: #ef4444">Error</span>
                <span class="pt-empty-sub">{{ error }}</span>
              </div>
            </td>
          </tr>

          <!-- Empty state -->
          <tr v-else-if="!items || items.length === 0" class="pt-empty-row">
            <td
              :colspan="columns.length + (hasActions ? 2 : 1)"
              class="pt-empty"
            >
              <div class="pt-empty-inner">
                <div class="pt-empty-icon">
                  <v-icon size="28" color="#CBD5E1">mdi-table-search</v-icon>
                </div>
                <span class="pt-empty-title">No data available</span>
                <span class="pt-empty-sub" v-if="searchInput"
                  >No results for "{{ searchInput }}"</span
                >
                <span class="pt-empty-sub" v-else>There are no records to display</span>
              </div>
            </td>
          </tr>

          <!-- Data rows -->
          <template v-else>
            <tr
              v-for="(item, idx) in items"
              :key="item.id || idx"
              class="pt-row"
            >
              <!-- Sr. No. -->
              <td class="pt-td pt-td-no">
                <span class="pt-no-badge">{{ (currentPage - 1) * itemsPerPage + idx + 1 }}</span>
              </td>

              <td
                v-for="col in columns"
                :key="col.key"
                class="pt-td"
                :class="[
                  col.align === 'right' ? 'pt-td-right' : '',
                  col.align === 'center' ? 'pt-td-center' : '',
                  col.hideOnMobile ? 'pt-hide-mobile' : '',
                  col.hideOnTablet ? 'pt-hide-tablet' : '',
                ]"
              >
                <!-- Vue 2 dynamic named slot via $scopedSlots -->
                <cell-renderer
                  :slot-fn="$scopedSlots['col-' + col.key]"
                  :item="item"
                  :index="idx"
                  :col="col"
                />
              </td>

              <!-- Actions slot or built-in actions -->
              <td v-if="hasActions" class="pt-td pt-td-actions">
                <div class="pt-actions-wrap">
                  <slot name="actions" :item="item" :index="idx">
                    <!-- Default Actions -->
                    <ActionButtons
                      :show-view="showView"
                      :show-edit="showEdit"
                      :show-delete="showDelete"
                      @view="$emit('view', item)"
                      @edit="$emit('edit', item)"
                      @delete="$emit('delete', item)"
                    />
                  </slot>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- ── Footer ─────────────────────────────────── -->
    <div class="pt-footer">
      <!-- Rows per page -->
      <div class="pt-footer-left">
        <span class="pt-footer-label">Show rows</span>
        <!-- Custom Row Selector -->
        <v-menu
          v-model="rppMenu"
          offset-y
          top
          nudge-top="8"
          content-class="pt-rpp-menu-shadow"
          transition="scale-transition"
          origin="bottom center"
        >
          <template v-slot:activator="{ on, attrs }">
            <div
              class="pt-rpp-trigger"
              v-bind="attrs"
              v-on="on"
              :class="{ 'pt-rpp-active': rppMenu }"
            >
              <span class="pt-rpp-val">{{ itemsPerPage }}</span>
              <v-icon
                size="18"
                class="pt-rpp-chevron"
                :class="{ 'pt-chevron-up': rppMenu }"
                >mdi-chevron-down</v-icon
              >
            </div>
          </template>

          <div
            class="pt-rpp-card"
            :style="{ '--brand': brandColor, '--brand-rgb': brandRgb }"
          >
            <div
              v-for="opt in rowOptions"
              :key="opt"
              class="pt-rpp-item"
              :class="{ 'item-selected': opt === itemsPerPage }"
              @click="onRppSelect(opt)"
            >
              <span class="pt-rpp-text">{{ opt }}</span>
              <div v-if="opt === itemsPerPage" class="pt-rpp-indicator"></div>
            </div>
          </div>
        </v-menu>
      </div>

      <!-- Range + Pagination -->
      <div class="pt-footer-right">
        <span class="pt-range-text">{{ rangeText }}</span>

        <div class="pt-pagination">
          <!-- First -->
          <button
            class="pt-page-btn"
            :disabled="currentPage <= 1"
            @click="goTo(1)"
            title="First page"
          >
            <v-icon size="18">mdi-chevron-double-left</v-icon>
          </button>
          <!-- Prev -->
          <button
            class="pt-page-btn"
            :disabled="currentPage <= 1"
            @click="goTo(currentPage - 1)"
            title="Previous page"
          >
            <v-icon size="18">mdi-chevron-left</v-icon>
          </button>

          <!-- Page numbers -->
          <template v-for="p in visiblePages">
            <span
              v-if="p === '...'"
              :key="'dot-' + p + Math.random()"
              class="pt-page-dots"
              >…</span
            >
            <button
              v-else
              :key="'pg-' + p"
              class="pt-page-btn pt-page-num"
              :class="{ 'pt-page-active': p === currentPage }"
              @click="goTo(p)"
            >
              {{ p }}
            </button>
          </template>

          <!-- Next -->
          <button
            class="pt-page-btn"
            :disabled="currentPage >= totalPages"
            @click="goTo(currentPage + 1)"
            title="Next page"
          >
            <v-icon size="18">mdi-chevron-right</v-icon>
          </button>
          <!-- Last -->
          <button
            class="pt-page-btn"
            :disabled="currentPage >= totalPages"
            @click="goTo(totalPages)"
            title="Last page"
          >
            <v-icon size="18">mdi-chevron-double-right</v-icon>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { debounce } from "lodash";
import ActionButtons from "./ActionButtons.vue";

export default {
  name: "PremiumTable",

  props: {
    columns: { type: Array, required: true },
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    error: { type: String, default: null },
    totalItems: { type: Number, default: 0 },
    page: { type: Number, default: 1 },
    itemsPerPage: { type: Number, default: 10 },
    searchable: { type: Boolean, default: true },
    search: { type: String, default: "" },
    searchPlaceholder: { type: String, default: "Search..." },
    addLabel: { type: String, default: "" },
    title: { type: String, default: "" },
    subtitle: { type: String, default: "" },
    skeletonRows: { type: Number, default: 6 },
    showView: { type: Boolean, default: false },
    showEdit: { type: Boolean, default: false },
    showDelete: { type: Boolean, default: false },
  },

  // Vue 2 — no emits option needed

  data() {
    return {
      searchInput: this.search || "",
      rowOptions: [10, 25, 50, 100],
      rppMenu: false,
      isDown: false,
      hasDragged: false,
      startX: 0,
      scrollLeftPos: 0,
    };
  },

  computed: {
    ...mapState("theme", ["isDark", "primaryColor", "buttonColor"]),

    brandColor() {
      return this.buttonColor || this.primaryColor || "#30384d";
    },

    hasActions() {
      return (
        !!this.$scopedSlots.actions ||
        this.showEdit ||
        this.showDelete ||
        this.showView
      );
    },

    currentPage() {
      return this.page;
    },

    brandRgb() {
      const hex = this.brandColor || "#0d9972";
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
        : "13, 153, 114";
    },

    totalPages() {
      if (!this.totalItems || !this.itemsPerPage) return 1;
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },

    rangeText() {
      if (this.loading) return "...";
      if (!this.items || this.items.length === 0 || !this.totalItems)
        return "0 of " + (this.totalItems || 0);
      const from = (this.currentPage - 1) * this.itemsPerPage + 1;
      const to = Math.min(from + this.items.length - 1, this.totalItems);
      return `${from}-${to} of ${this.totalItems}`;
    },

    visiblePages() {
      const total = this.totalPages;
      const cur = this.currentPage;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
      if (cur <= 4) return [1, 2, 3, 4, 5, "...", total];
      if (cur >= total - 3)
        return [1, "...", total - 4, total - 3, total - 2, total - 1, total];
      return [1, "...", cur - 1, cur, cur + 1, "...", total];
    },
  },

  watch: {
    search(newVal) {
      this.searchInput = newVal;
    },
  },

  methods: {
    goTo(page) {
      if (page < 1 || page > this.totalPages) return;
      this.$emit("update:page", page);
    },

    onRppSelect(val) {
      this.$emit("update:items-per-page", val);
      this.$emit("update:page", 1);
      this.rppMenu = false;
    },

    onRppChange(e) {
      this.$emit("update:itemsPerPage", parseInt(e.target.value, 10));
      this.$emit("update:page", 1);
    },

    onSearchDebounced: debounce(function () {
      this.$emit("search", this.searchInput);
      this.$emit("update:page", 1);
    }, 450),

    clearSearch() {
      this.searchInput = "";
      this.$emit("search", "");
      this.$emit("update:page", 1);
    },

    randomWidth() {
      const opts = [45, 60, 70, 55, 65];
      return opts[Math.floor(Math.random() * opts.length)];
    },

    onMouseDown(e) {
      this.isDown = true;
      this.hasDragged = false;
      const wrap = this.$refs.tableWrap;
      if (!wrap) return;
      wrap.classList.add("pt-dragging");
      this.startX = e.pageX - wrap.offsetLeft;
      this.scrollLeftPos = wrap.scrollLeft;
    },

    onMouseLeave() {
      this.isDown = false;
      if (this.$refs.tableWrap) {
        this.$refs.tableWrap.classList.remove("pt-dragging");
      }
    },

    onMouseUp() {
      this.isDown = false;
      if (this.$refs.tableWrap) {
        this.$refs.tableWrap.classList.remove("pt-dragging");
      }
    },

    onMouseMove(e) {
      if (!this.isDown) return;
      e.preventDefault();
      const wrap = this.$refs.tableWrap;
      if (!wrap) return;
      const x = e.pageX - wrap.offsetLeft;
      
      // Only count as a drag if moved more than 3px to avoid suppressing normal clicks
      if (Math.abs(x - this.startX) > 3) {
        this.hasDragged = true;
      }
      
      const walk = (x - this.startX) * 1.5; // Scroll speed multiplier
      wrap.scrollLeft = this.scrollLeftPos - walk;
    },

    onClickCapture(e) {
      // If the user was dragging, prevent the click from firing on inner elements
      if (this.hasDragged) {
        e.stopPropagation();
        e.preventDefault();
      }
    },
  },

  // Register the cell renderer as a local component
  // This is the Vue 2 way to handle dynamic named slots
  components: {
    ActionButtons,
    CellRenderer: {
      functional: true,
      props: ["slotFn", "item", "index", "col"],
      render(h, ctx) {
        const { slotFn, item, index, col } = ctx.props;
        // If consumer provided a custom slot for this column, use it
        if (slotFn) {
          return slotFn({ item, index });
        }
        // Otherwise fallback: col.render function or plain value
        if (col.render) {
          return h("span", {
            domProps: { innerHTML: col.render(item, index) },
          });
        }
        const val =
          col.key && item[col.key] !== undefined ? item[col.key] : "—";
        return h("span", String(val));
      },
    },
  },
};
</script>

<style scoped>
/* ── Wrapper ───────────────────────────── */
.pt-wrapper {
  border-radius: 16px;
  overflow: hidden;
  transition: background 0.2s, border-color 0.2s;
}
.pt-light {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.07), 0 2px 8px rgba(0, 0, 0, 0.04);
}
.pt-dark {
  background: var(--surface, #171717);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
}

/* ── Header ───────────────────────────── */
.pt-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border, #e2e8f0);
}
.pt-dark .pt-header {
  border-bottom-color: rgba(255, 255, 255, 0.07);
}
.pt-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}
.pt-header-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.pt-title {
  font-family: "Bricolage Grotesque", sans-serif;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--tx, #111827);
}
.pt-dark .pt-title {
  color: #f1f5f9;
}
.pt-subtitle {
  font-size: 12px;
  color: var(--tx3, #9ca3af);
  margin-top: 2px;
}

/* ── Search Box ───────────────────────── */
.pt-search-box {
  position: relative;
  display: flex;
  align-items: center;
  background: var(--surface2, #f8fafc);
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 10px;
  height: 38px;
  padding: 0 12px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 220px;
}
.pt-dark .pt-search-box {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
}
.pt-search-box:focus-within {
  border-color: var(--brand);
  background: var(--surface, #ffffff);
  box-shadow: 0 4px 12px rgba(var(--brand-rgb), 0.12);
}
.pt-dark .pt-search-box:focus-within {
  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.pt-search-icon {
  color: #9ca3af !important;
  margin-right: 8px;
  transition: color 0.2s;
}
.pt-search-box:focus-within .pt-search-icon {
  color: var(--brand) !important;
}

.pt-search-input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--tx, #111827);
  width: 100%;
  padding-right: 24px; /* Space for clear btn */
}
.pt-dark .pt-search-input {
  color: #f1f5f9;
}
.pt-search-input::placeholder {
  color: #9ca3af;
  font-weight: 400;
}

.pt-search-clear {
  position: absolute;
  right: 8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid rgba(var(--brand));
  background: #efebeb;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}
.pt-dark .pt-search-clear {
  background: rgba(255, 255, 255, 0.12);
  color: #94a3b8;
}
.pt-search-clear:hover {
  background: var(--brand) !important;
  color: #fff !important;
  transform: scale(1.1);
}
.pt-dark .pt-search-clear:hover {
  background: rgba(255, 255, 255, 0.2);
  color: #f8fafc;
}

/* Using global .pt-add-btn from style.css */

/* ── Table ────────────────────────────── */
.sb-nav-scroll {
  flex: 1;
  overflow-y: auto;
}
.pt-table-wrap {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  position: relative;
  padding-bottom: 4px;
  /* cursor: grab; */
}
/* .pt-table-wrap:active {
  cursor: grabbing;
} */

/* Scrollbar styles moved to unscoped block at the end of the file for reliability */
.pt-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
  min-width: 480px;
}

/* ── Head ─────────────────────────────── */
.pt-thead {
  position: sticky;
  top: 0;
  z-index: 2;
}
.pt-th {
  padding: 11px 16px;
  text-align: left;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #9ca3af;
  background: var(--surface2, #f8fafc);
  border-bottom: 1px solid var(--border, #e2e8f0);
  white-space: nowrap;
  overflow: hidden;
}
.pt-dark .pt-th {
  background: var(--surface2, #1F1F1F);
  border-bottom-color: rgba(255, 255, 255, 0.07);
  color: var(--tx4, #737373);
}
.pt-th-actions {
  text-align: right;
}
.pt-th-right {
  text-align: right;
}
.pt-th-center {
  text-align: center;
}

/* ── Rows ─────────────────────────────── */
.pt-row {
  transition: background 0.15s;
}
.pt-light .pt-row:hover {
  background: #f8fafc;
}
.pt-dark .pt-row:hover {
  background: rgba(255, 255, 255, 0.025);
}
.pt-row:not(:last-child) td {
  border-bottom: 1px solid var(--border2, #f1f5f9);
}
.pt-dark .pt-row:not(:last-child) td {
  border-bottom-color: rgba(255, 255, 255, 0.05);
}

.pt-th-no {
  text-align: center;
  width: 52px;
  min-width: 52px;
}
.pt-td-no {
  text-align: center;
  width: 52px;
  min-width: 52px;
  max-width: none !important;
  overflow: visible !important;
}
.pt-no-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: 22px;
  padding: 0 5px;
  border-radius: 6px;
  font-size: 11.5px;
  font-weight: 700;
  color: var(--tx3, #6B7280);
  background: var(--surface2, #F1F5F9);
}
.pt-dark .pt-no-badge {
  background: rgba(255,255,255,0.07);
  color: #64748B;
}

.pt-td {
  padding: 13px 16px;
  font-size: 13px;
  color: var(--tx2, #374151);
  vertical-align: middle;
  overflow: hidden;
}
.pt-dark .pt-td {
  color: var(--tx2, #D4D4D4);
}
.pt-td-actions {
  text-align: right;
  max-width: none; /* actions column must not be clipped */
  overflow: visible;
  width: 120px;
}
.pt-td-right {
  text-align: right;
}
.pt-td-center {
  text-align: center;
}

/* ── Actions ──────────────────────────── */
.pt-actions-wrap {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
}
.pt-action-btn {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid var(--border, #e2e8f0);
  background: var(--surface, #ffffff);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}
.pt-dark .pt-action-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
}
.pt-action-btn:hover {
  background: var(--surface2, #f8fafc);
  border-color: #cbd5e1;
}
.pt-dark .pt-action-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}
.pt-action-view:hover {
  color: #3b82f6;
}
.pt-action-delete {
  border-color: rgba(239, 68, 68, 0.2);
}
.pt-action-delete:hover {
  background: #fef2f2;
  border-color: rgba(239, 68, 68, 0.4);
}
.pt-dark .pt-action-delete:hover {
  background: rgba(239, 68, 68, 0.15);
}

/* ── Empty / Error ────────────────────── */
.pt-empty-row {
  /* Keep entire row at min the wrapper's visible width */
}
.pt-empty {
  padding: 0 !important;
  /* Reset so the sticky child can control sizing */
  position: static;
}

/* The inner div becomes sticky so it always
   sits within the visible scroll-area, not off to the right */
.pt-empty-inner {
  position: sticky;
  left: 0;
  /* Width = full viewport minus the sidebar (~56px on mobile) */
  width: calc(100vw - 80px);
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px 24px;
}
.pt-empty-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--surface2, #F8FAFC);
  border: 1px solid var(--border, #E2E8F0);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
}
.pt-dark .pt-empty-icon {
  background: rgba(255,255,255,0.05);
  border-color: rgba(255,255,255,0.08);
}
.pt-empty-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--tx2, #374151);
  letter-spacing: -0.01em;
  text-align: center;
}
.pt-dark .pt-empty-title {
  color: var(--tx3, #A3A3A3);
}
.pt-empty-sub {
  font-size: 12.5px;
  color: #9ca3af;
  text-align: center;
}

/* ── Shimmer skeleton ─────────────────── */
.pt-shimmer {
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: ptShimmer 1.4s ease-in-out infinite;
  border-radius: 6px;
  display: inline-block;
}
.pt-dark .pt-shimmer {
  background: linear-gradient(90deg, var(--surface2, #1F1F1F) 25%, var(--surface3, #262626) 50%, var(--surface2, #1F1F1F) 75%);
  background-size: 200% 100%;
}
@keyframes ptShimmer {
  from {
    background-position: 200% 0;
  }
  to {
    background-position: -200% 0;
  }
}

/* ── Footer ───────────────────────────── */
.pt-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 20px;
  border-top: 1px solid var(--border, #e2e8f0);
}
.pt-dark .pt-footer {
  border-top-color: rgba(255, 255, 255, 0.07);
}
.pt-footer-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.pt-footer-label {
  font-size: 12.5px;
  font-weight: 600;
  color: #9ca3af;
  white-space: nowrap;
}
.pt-rpp-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 72px;
  height: 38px;
  padding: 0 10px 0 14px;
  border: 1.5px solid var(--border, #e2e8f0);
  border-radius: 12px;
  background: var(--surface, #ffffff);
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
}
.pt-rpp-trigger:hover {
  border-color: #cbd5e1;
  background: var(--surface2, #f8fafc);
}
.pt-rpp-active {
  border-color: var(--brand) !important;
  box-shadow: 0 2px 10px rgba(var(--brand-rgb), 0.25);
}

.pt-rpp-val {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--tx, #111827);
}
.pt-rpp-chevron {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), color 0.2s;
  color: #9ca3af !important;
}
.pt-rpp-active .pt-rpp-chevron {
  transform: rotate(180deg);
  color: var(--brand) !important;
}

.pt-rpp-menu-shadow {
  box-shadow: none !important;
  background: transparent !important;
  overflow: visible;
}

.pt-rpp-card {
  background: var(--surface, #ffffff);
  border-radius: 14px;
  padding: 10px 8px;
  min-width: 80px;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.04);
  overflow: visible;
}
.pt-dark .pt-rpp-card {
  background: var(--surface2, #1F1F1F);
  border-color: rgba(255, 255, 255, 0.08);
}

.pt-rpp-item {
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;
  margin-bottom: 2px;
}
.pt-rpp-item:last-child {
  margin-bottom: 0;
}
.pt-rpp-item:hover {
  background: var(--surface2, #f8fafc);
}
.pt-dark .pt-rpp-item:hover {
  background: rgba(255, 255, 255, 0.05);
}

.item-selected {
  background: rgba(var(--brand-rgb), 0.10) !important;
  transform: translateY(-1px);
}
.item-selected .pt-rpp-text {
  color: var(--brand);
  font-weight: 800;
}
.pt-rpp-text {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--tx2, #4b5563);
}
.item-selected .pt-rpp-text {
  color: var(--brand);
  font-weight: 700;
}
.pt-dark .pt-rpp-text {
  color: var(--tx2, #D4D4D4);
}

.pt-rpp-indicator {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--brand);
}

.pt-footer-right {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}
.pt-range-text {
  font-size: 12.5px;
  color: #9ca3af;
  white-space: nowrap;
}

/* ── Pagination ───────────────────────── */
.pt-pagination {
  display: flex;
  align-items: center;
  gap: 2px;
}
.pt-page-btn {
  min-width: 30px;
  height: 30px;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 12.5px;
  font-weight: 500;
  transition: all 0.15s;
  padding: 0 6px;
  font-family: "Instrument Sans", sans-serif;
}
.pt-page-btn:hover:not(:disabled) {
  background: var(--surface2, #f8fafc);
  color: #111827;
}
.pt-dark .pt-page-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.07);
  color: #f1f5f9;
}
.pt-page-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.pt-page-active {
  background: var(--btn-color, var(--brand, #30384d)) !important;
  color: #ffffff !important;
  font-weight: 700 !important;
}
.pt-page-dots {
  padding: 0 4px;
  color: #9ca3af;
  font-size: 13px;
  user-select: none;
}

/* ── Responsive ───────────────────────── */

/* Mobile & Tablet (≤960px) */
@media (max-width: 960px) {
  .pt-hide-mobile, .pt-hide-tablet {
    display: none !important;
  }

  /* Header: stack into rows */
  .pt-header {
    padding: 12px 14px;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  /* Title row */
  .pt-header-left {
    width: 100%;
  }

  /* Controls row: search takes full width, rest wrap below */
  .pt-header-right {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    width: 100%;
  }

  /* Search box fills available space on tablet */
  .pt-search-box {
    min-width: 0;
    flex: 1 1 auto;
    height: 38px;
    order: -1; /* Always first */
  }

  /* Extra slot items row (filters, export, add) */
  .pt-header-right > *:not(.pt-search-box) {
    flex-shrink: 0;
    flex-grow: 1; /* Allow them to grow if needed */
  }

  /* Add button: compact */
  .pt-add-btn {
    height: 38px !important;
    padding: 0 12px 0 10px !important;
    font-size: 13px !important;
    white-space: nowrap;
    flex-shrink: 0;
    width: 100%;
    justify-content: center;
  }

  /* Export button compact */
  .pt-export-btn {
    width: 38px !important;
    height: 38px !important;
    flex-shrink: 0;
  }

  /* Table cells */
  .pt-th, .pt-td {
    padding: 10px 10px;
    font-size: 12px;
  }
  .pt-th-no, .pt-td-no {
    width: 40px;
    min-width: 40px;
    padding: 10px 6px;
  }
  .pt-no-badge {
    min-width: 24px;
    font-size: 11px;
  }

  /* Footer */
  .pt-footer {
    padding: 10px 14px;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  .pt-footer-left, .pt-footer-right {
    width: 100%;
    justify-content: space-between;
  }
  .pt-pagination .pt-page-btn.pt-page-num {
    display: none;
  }
  .pt-pagination .pt-page-btn.pt-page-num.pt-page-active {
    display: flex;
  }
}

/* Mobile (≤600px) */
@media (max-width: 600px) {
  .pt-search-box {
    flex: 1 1 100%;
    max-width: 100%;
  }

  /* On small screens give the sticky empty-state the full visible width */
  .pt-empty-inner {
    width: calc(100vw - 72px); /* viewport minus sidebar */
    padding: 32px 16px;
  }

  /* Show the horizontal scrollbar on mobile so users know they can scroll */
  .pt-table-wrap {
    scrollbar-width: thin;
    scrollbar-color: #CBD5E1 transparent;
  }
  .pt-table-wrap::-webkit-scrollbar {
    display: block;
    height: 4px;
  }
  .pt-table-wrap::-webkit-scrollbar-track {
    background: transparent;
  }
  .pt-table-wrap::-webkit-scrollbar-thumb {
    background: #CBD5E1;
    border-radius: 4px;
  }
  .pt-dark .pt-table-wrap::-webkit-scrollbar-thumb {
    background: rgba(255,255,255,0.15);
  }
}
</style>
